// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 6c7fed75-7a43-486c-87af-b567f2936c34

// ---------------------------------------------------------------------------
// Primordia — Cloud Cost Model
// ---------------------------------------------------------------------------
// Three tabs, three different kinds of truth, never blended:
//
//   Estimate     — what the workload SHOULD cost at list price. Sized from the
//                  current-state server fact sheets (ITComponent) or from a
//                  target-state bill of materials (the Epic CSG import), then
//                  priced by POST /pricing. If that endpoint is not wired yet
//                  we say so on screen and fall back to an internal rate card
//                  that is labelled as an internal estimate everywhere it is
//                  used. Unpriceable lines are ALWAYS listed — a total that
//                  quietly drops SKUs is worse than no total at all.
//
//   Actual       — real invoiced spend from a connected client cloud account.
//                  Not built. This tab renders the honest empty state and the
//                  exact access it will require. It fabricates nothing.
//
//   Adjustments  — what the client actually pays: PPA/EDP discount off list
//                  (overall and per service category), a support plan whose
//                  percentage the USER enters (support rates change; we do not
//                  invent them), and free-form additional cost lines. Produces
//                  the waterfall list -> less discount -> plus support -> plus
//                  additional = effective, persisted per tenant.
//
// PERFORMANCE — the reason this screen was rewritten.
// The previous version mounted one MUI TextField per server per sizing
// attribute. At the current tenant ceiling (7,305 servers) that is ~24k live
// inputs and the tab hangs the browser. Two changes fix it structurally:
//
//   1. AGGREGATE. Servers are never priced individually. Identical
//      (vCPU, RAM, storage) shapes collapse into one priced line carrying a
//      quantity, so an estate of thousands becomes tens of lines. Aggregation
//      is exact — no rounding, nothing invented — and every line reports how
//      many fact sheets it represents.
//   2. PAGINATE. Every table on this screen is capped at ROWS_PER_PAGE
//      (default 100) rows. The only editable control on a priced row is the
//      hours/month field, so the on-screen input count is bounded by the page
//      size regardless of tenant size. Totals, exports and the unpriced list
//      always cover the FULL set, not the visible page.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Stack, Tabs, Tab, TextField, MenuItem, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
  Chip, Alert, AlertTitle, Divider, Tooltip, IconButton, LinearProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import store from './store';
import { exportCsv, printElementToPdf } from './exports';
import { getConfig } from './config';
import { getIdToken } from './auth';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const AMBER = '#B26A00';
const RED = '#C62828';
const GREEN = '#2E7D32';
const HEAD = { color: 'sectionHeader.main', fontWeight: 700 };
const headerSx = { '& th': { fontWeight: 700, color: 'sectionHeader.main', whiteSpace: 'nowrap' } };
const primaryBtn = { bgcolor: BLUE, '&:hover': { bgcolor: NAVY } };

// ---- Paging ----------------------------------------------------------------
// The single lever that keeps a 7,305-server tenant from mounting 24k inputs.
const ROWS_PER_PAGE = 100;
const ROWS_PER_PAGE_OPTIONS = [50, 100, 250];

const HOURS_ALWAYS_ON = 730;   // 365 * 24 / 12
const HOURS_BUSINESS = 200;    // ~10h x 20 working days, non-prod scheduled off

// ---------------------------------------------------------------------------
// FALLBACK RATE CARD — INTERNAL ESTIMATE ONLY, NEVER A QUOTE.
//
// This exists for exactly one situation: POST /pricing is not reachable. It is
// a planning-grade decomposition of general-purpose on-demand Linux list rates
// into $/vCPU-hour + $/GB-RAM-hour + $/GB-month, so an arbitrary right-sizing
// can be given an order of magnitude. It is NOT the client's price, it knows
// nothing about SKUs, commitments, regions or agreements, and anything derived
// from it is badged "internal estimate" on screen and in every export.
// ---------------------------------------------------------------------------
const FALLBACK_RATE_CARD = {
  aws: {
    label: 'AWS',
    defaultRegion: 'us-east-1',
    computeVcpuHour: 0.032,
    computeGbRamHour: 0.004,
    storageGbMonth: 0.08,
  },
  azure: {
    label: 'Azure',
    defaultRegion: 'eastus',
    computeVcpuHour: 0.033,
    computeGbRamHour: 0.0042,
    storageGbMonth: 0.095,
  },
};

// Support plan names only. The PERCENTAGE IS DELIBERATELY NOT HERE — AWS and
// Azure both change support pricing and both are tiered on spend bands, so the
// consultant enters the blended percentage they have confirmed.
const SUPPORT_TIERS = {
  aws: ['Developer', 'Business', 'Enterprise On-Ramp', 'Enterprise'],
  azure: ['Developer', 'Standard', 'Professional Direct', 'Microsoft Unified'],
};

const ADDITIONAL_CATEGORIES = [
  'Managed services',
  'Partner / MSP margin',
  'Licensing',
  'Data transfer / egress',
  'Third-party tooling',
  'Other',
];

// ---------------------------------------------------------------------------
// What connecting a real cloud account will require. Reference data for the
// Actual tab and its CSV — no spend, no samples, no invented numbers.
// ---------------------------------------------------------------------------
const CONNECTION_REQUIREMENTS = [
  {
    cloud: 'AWS',
    requirement: 'Cross-account IAM role',
    detail: 'A read-only IAM role in the client account that Primordia\'s account assumes via sts:AssumeRole. The trust policy must require an ExternalId that Primordia issues per engagement — this is what stops the confused-deputy problem.',
  },
  {
    cloud: 'AWS',
    requirement: 'ExternalId',
    detail: 'A unique, non-guessable value tied to this tenant, set on the role trust policy. Primordia stores it alongside the role ARN and sends it on every AssumeRole call.',
  },
  {
    cloud: 'AWS',
    requirement: 'Cost Explorer (rollups)',
    detail: 'ce:GetCostAndUsage, ce:GetCostAndUsageWithResources, ce:GetDimensionValues, ce:GetTags, ce:GetCostForecast. Gives daily/monthly rollups by service, account and tag, typically ~24h behind.',
  },
  {
    cloud: 'AWS',
    requirement: 'CUR 2.0 (line-item detail)',
    detail: 'A Cost and Usage Report 2.0 export delivered to an S3 bucket, queried through Athena (Glue catalog + an Athena workgroup). Needs s3:GetObject/ListBucket on the report prefix plus athena and glue read permissions. This is the only source with resource-level, amortised, discount-aware line items.',
  },
  {
    cloud: 'AWS',
    requirement: 'Scope',
    detail: 'Grant at the payer (management) account so consolidated billing covers every member account. Member-account-only access sees only that account\'s spend.',
  },
  {
    cloud: 'Azure',
    requirement: 'Entra ID app registration',
    detail: 'An application registration (service principal) created in the client\'s Entra ID tenant, authenticated by workload identity federation so no client secret has to be shared or stored.',
  },
  {
    cloud: 'Azure',
    requirement: 'Cost Management Reader',
    detail: 'The service principal granted the Cost Management Reader role at the billing account, management group, or subscription scope that the engagement covers. Read-only; it cannot see or change workloads.',
  },
  {
    cloud: 'Azure',
    requirement: 'Cost Management Query API (rollups)',
    detail: 'Actual and amortised cost queries by service, resource group and tag, at the granted scope.',
  },
  {
    cloud: 'Azure',
    requirement: 'Exports (line-item detail)',
    detail: 'A Cost Management scheduled export (FOCUS or amortised cost) to a storage account, plus read access to that container — the Azure equivalent of CUR for resource-level detail.',
  },
  {
    cloud: 'Azure',
    requirement: 'Scope',
    detail: 'Billing-account or management-group scope covers every subscription in the enrollment; a single subscription grant covers only that subscription.',
  },
];

// ---- Small helpers ---------------------------------------------------------
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const money2 = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (n) => (Number(n) || 0).toLocaleString();

// null (not zero) when the value was never recorded — the difference between
// "this server has no storage" and "nobody wrote the storage down".
function numOrNull(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
const numOr0 = (v) => numOrNull(v) || 0;
const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';
const pctOrNull = (v) => {
  if (isBlank(v)) return null;
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
};
const fieldsOf = (fs) => (fs && (fs.fields || fs.data)) || {};
const newId = (p) => `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// ---------------------------------------------------------------------------
// Line construction — the aggregation that replaces per-server inputs
// ---------------------------------------------------------------------------

// Current state: one line per distinct (client, vCPU, RAM, storage) shape.
// Exact match only; nothing is rounded into a neighbouring bucket. Servers with
// no vCPU or no RAM recorded cannot be priced and are collected separately so
// they end up in the unpriced list instead of silently scoring zero.
//
// The client is part of the key, not decoration. In the master workspace
// `servers` spans every tenant, and grouping on shape alone folded one client's
// 200 servers together with another's 50 into a single priced line — one
// quantity, one dollar figure, and `sample` names drawn from different health
// systems. That number belongs to nobody, and it is one copy-paste away from a
// client deliverable. In a single-client workspace every row carries the same
// tenant, so this changes nothing there.
export function buildCurrentStateLines(servers) {
  const groups = new Map();
  const noSizing = [];
  (servers || []).forEach((s) => {
    const f = fieldsOf(s);
    const vcpu = numOrNull(f.vcpu);
    const ramGb = numOrNull(f.ramGb);
    const storageGb = numOrNull(f.storageGb);
    if (vcpu === null || ramGb === null) { noSizing.push(s); return; }
    const shape = `${vcpu}|${ramGb}|${storageGb === null ? 'na' : storageGb}`;
    const tenantId = s.tenantId || '';
    const key = `${tenantId}|${shape}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        id: `cur-${key}`,
        // The id before the client entered the key. Only used to carry a
        // consultant's saved SKU mapping across this change; see `lines`.
        legacyId: `cur-${shape}`,
        kind: 'compute',
        source: 'current',
        tenantId,
        sku: '',        // fact sheets record shape, not an instance type
        diskType: '',
        vcpu,
        ramGb,
        storageGb: storageGb === null ? 0 : storageGb,
        storageKnown: storageGb !== null,
        qty: 0,
        sample: [],
        label: `${fmt(vcpu)} vCPU · ${fmt(ramGb)} GB RAM · ${storageGb === null ? 'storage not recorded' : `${fmt(storageGb)} GB`}`,
      };
      groups.set(key, g);
    }
    g.qty += 1;
    if (g.sample.length < 3) g.sample.push(s.name);
  });
  const lines = [...groups.values()].sort((a, b) => (b.qty * b.vcpu) - (a.qty * a.vcpu));
  return { lines, noSizing };
}

// Target state: the Epic CSG bill of materials as imported on the CSG screen.
// vCPU/RAM are only present when that workbook stated them; when it did not,
// the line carries a SKU and nothing else, which the internal rate card cannot
// price. That is exactly the case live pricing exists to solve, and the line
// goes into the unpriced list rather than being guessed at.
export function buildTargetStateLines(csg) {
  if (!csg) return { lines: [], noSizing: [] };
  const lines = [];
  (csg.lines || []).forEach((l, i) => {
    const qty = numOr0(l.quantity);
    if (!qty) return;
    const osDisk = numOrNull(l.osDiskGb);
    lines.push({
      id: `csg-c-${l.id || i}`,
      kind: 'compute',
      source: 'target',
      sku: l.cloudResource || '',
      diskType: l.osDiskType || '',
      vcpu: numOrNull(l.vcpuEach),
      ramGb: numOrNull(l.ramGbEach),
      storageGb: osDisk === null ? 0 : osDisk,
      storageKnown: osDisk !== null,
      qty,
      sample: [],
      label: [l.epicTier, l.stamp, l.epicResource].filter(Boolean).join(' · ') || l.cloudResource || 'Compute line',
    });
  });
  (csg.storageLines || []).forEach((l, i) => {
    const cap = numOrNull(l.capacityGb);
    lines.push({
      id: `csg-s-${l.id || i}`,
      kind: 'storage',
      source: 'target',
      sku: l.storageType || '',
      diskType: l.storageType || '',
      vcpu: null,
      ramGb: null,
      storageGb: cap === null ? 0 : cap,
      storageKnown: cap !== null,
      qty: numOr0(l.quantity) || 1,
      sample: [],
      label: [l.epicTier, l.stamp, l.epicResource].filter(Boolean).join(' · ') || l.storageType || 'Storage line',
    });
  });
  return { lines, noSizing: [] };
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

// Internal fallback. Returns priced:false with a reason rather than a zero
// whenever the card genuinely cannot price the line.
export function fallbackPriceLine(line, rate) {
  if (line.kind === 'storage') {
    if (!line.storageKnown) {
      return {
        compute: 0, storage: 0, total: 0, priced: false, caveat: '',
        reason: `Storage line "${line.sku || line.label}" states no capacity — nothing to price.`,
      };
    }
    const storage = line.storageGb * line.qty * rate.storageGbMonth;
    return { compute: 0, storage, total: storage, priced: true, reason: '', caveat: '' };
  }
  if (line.vcpu === null || line.ramGb === null) {
    return {
      compute: 0, storage: 0, total: 0, priced: false, caveat: '',
      reason: line.sku
        ? `SKU "${line.sku}" cannot be priced from the internal rate card — the card prices per vCPU/GB and this line states neither. Needs live pricing.`
        : 'No vCPU/RAM recorded — cannot be priced.',
    };
  }
  const compute = ((line.vcpu * rate.computeVcpuHour) + (line.ramGb * rate.computeGbRamHour)) * line.hours * line.qty;
  const storage = line.storageKnown ? line.storageGb * line.qty * rate.storageGbMonth : 0;
  return {
    compute,
    storage,
    total: compute + storage,
    priced: true,
    reason: '',
    caveat: line.storageKnown ? '' : 'Storage not recorded — storage cost excluded from this line.',
  };
}

const firstNum = (...vals) => {
  for (const v of vals) {
    const n = typeof v === 'string' ? parseFloat(v) : v;
    if (Number.isFinite(n)) return n;
  }
  return null;
};

// Normalise what POST /pricing (op:'estimate') returns into the shape this
// screen renders. The service reports per-line compute/storage, an `unpriced`
// array of the things it refused to guess at, and totals that are a FLOOR
// whenever `unpriced` is non-empty. All three are carried through verbatim —
// nothing it declined to price is ever absorbed into a number here.
export function mergeLivePricing(lines, payload) {
  const apiLines = new Map();
  (Array.isArray(payload.lines) ? payload.lines : []).forEach((r, i) => {
    if (!r) return;
    apiLines.set(String(r.id !== undefined && r.id !== null ? r.id : i), r);
  });

  // The service keys unpriced items back to the line they came from, and one
  // line can fail on compute AND on a disk, so they are grouped not replaced.
  const issuesByLine = new Map();
  const orphans = [];
  (Array.isArray(payload.unpriced) ? payload.unpriced : []).forEach((raw) => {
    const u = typeof raw === 'string' ? { item: raw, reason: 'Returned as unpriceable by the pricing service.' } : (raw || {});
    const key = [u.lineId, u.id].find((k) => k !== undefined && k !== null);
    if (key === undefined) orphans.push(u);
    else {
      const arr = issuesByLine.get(String(key)) || [];
      arr.push(u);
      issuesByLine.set(String(key), arr);
    }
  });

  const known = new Set(lines.map((l) => String(l.id)));
  const rows = [];
  const unpriced = [];
  const caveats = [];

  lines.forEach((line) => {
    const r = apiLines.get(String(line.id));
    // A storage-only line has no instance type by definition; the service
    // flagging it as missing one is expected, not a pricing failure.
    const issues = (issuesByLine.get(String(line.id)) || [])
      .filter((u) => !(line.kind === 'storage' && u.kind === 'compute' && !u.item));

    if (!r) {
      rows.push({ line, compute: 0, storage: 0, total: 0, priced: false, partial: false });
      unpriced.push({
        id: `${line.id}:missing`,
        tenantId: line.tenantId,
        label: line.label,
        sku: line.sku,
        qty: line.qty,
        reason: issues.length
          ? issues.map((u) => u.reason).filter(Boolean).join('; ')
          : 'The pricing service returned no line for this item.',
      });
      return;
    }

    const compute = firstNum(r.compute && r.compute.monthly, r.compute, r.computeMonthly) || 0;
    const storage = firstNum(r.storage && r.storage.monthly, r.storage, r.storageMonthly) || 0;
    const amount = compute + storage;

    issues.forEach((u, i) => {
      unpriced.push({
        id: `${line.id}:${u.kind || 'item'}:${u.item || i}`,
        tenantId: line.tenantId,
        label: line.label,
        sku: u.item || line.sku || '',
        qty: line.qty,
        reason: `${u.kind ? `${u.kind}: ` : ''}${u.reason || 'Returned as unpriceable by the pricing service.'}`,
      });
    });

    const priced = issues.length === 0;
    if (!priced && amount > 0) {
      caveats.push(`${line.label} — priced in part only (${money(amount)} of it). The rest is in the unpriced list.`);
    }
    rows.push({ line, compute, storage, total: amount, priced, partial: !priced && amount > 0 });
  });

  // Anything flagged against a line we never sent is still reported.
  orphans.forEach((u, i) => {
    unpriced.push({
      id: `orphan-${i}`,
      label: u.name || u.item || 'Unidentified item',
      sku: u.item || '',
      qty: '',
      reason: u.reason || 'Returned as unpriceable by the pricing service.',
    });
  });
  issuesByLine.forEach((arr, id) => {
    if (known.has(id)) return;
    arr.forEach((u, i) => {
      unpriced.push({
        id: `orphan-${id}-${i}`,
        label: u.name || u.item || id,
        sku: u.item || '',
        qty: '',
        reason: u.reason || 'Returned as unpriceable by the pricing service.',
      });
    });
  });

  // Prefer the service's own totals — they are what it will stand behind —
  // and say so loudly when its line items do not reconcile to them.
  const t = payload.totals || {};
  const sumCompute = rows.reduce((a, r) => a + r.compute, 0);
  const sumStorage = rows.reduce((a, r) => a + r.storage, 0);
  const apiCompute = firstNum(t.computeMonthly, payload.computeMonthly);
  const apiStorage = firstNum(t.storageMonthly, payload.storageMonthly);
  const compute = apiCompute === null ? sumCompute : apiCompute;
  const storage = apiStorage === null ? sumStorage : apiStorage;
  const apiTotal = firstNum(t.listMonthly, t.monthly, payload.total, payload.totalMonthly);
  const total = apiTotal === null ? compute + storage : apiTotal;
  if (Math.abs(total - (sumCompute + sumStorage)) > 1) {
    caveats.push(`The pricing service reported ${money(total)} but its line items sum to ${money(sumCompute + sumStorage)}. Reconcile before this number is quoted.`);
  }

  return {
    rows,
    unpriced,
    caveats,
    totals: { compute, storage, total },
    complete: t.complete !== false && unpriced.length === 0,
    warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
    assumptions: Array.isArray(payload.assumptions) ? payload.assumptions : [],
    cached: !!payload.cached,
    source: payload.source || '',
  };
}

// ---------------------------------------------------------------------------
// Adjustments persistence — per tenant, localStorage, no DB table
// ---------------------------------------------------------------------------
const adjKey = (tenantId) => `primordia.costadj.${tenantId}`;

const EMPTY_ADJ = {
  version: 1,
  ppa: { overall: '', compute: '', storage: '', dataTransfer: '', effectiveDate: '', note: '' },
  support: { tier: '', pct: '' },
  dataTransferList: '',
  lines: [],
  // Live pricing prices published SKUs. These let a consultant map a sizing
  // shape to a real instance type / disk type so it CAN be priced for real,
  // instead of the tool guessing one.
  skus: {},
  diskType: '',
};

const blankAdj = () => ({
  ...EMPTY_ADJ,
  ppa: { ...EMPTY_ADJ.ppa },
  support: { ...EMPTY_ADJ.support },
  lines: [],
  skus: {},
});

export function readAdjustments(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return blankAdj();
    const p = JSON.parse(raw) || {};
    return {
      version: 1,
      ppa: { ...EMPTY_ADJ.ppa, ...(p.ppa || {}) },
      support: { ...EMPTY_ADJ.support, ...(p.support || {}) },
      dataTransferList: p.dataTransferList || '',
      lines: Array.isArray(p.lines) ? p.lines : [],
      skus: (p.skus && typeof p.skus === 'object') ? p.skus : {},
      diskType: p.diskType || '',
    };
  } catch (e) {
    return blankAdj();
  }
}

function writeAdjustments(key, adj) {
  try { localStorage.setItem(key, JSON.stringify(adj)); } catch (e) { /* quota — state still holds it */ }
}

// The waterfall. Returns every intermediate step so the UI can never show a
// discounted number without also showing the discount that produced it.
export function buildWaterfall(totals, adj) {
  const compute = Number(totals.compute) || 0;
  const storage = Number(totals.storage) || 0;
  const dataTransfer = numOr0(adj.dataTransferList);
  const list = compute + storage + dataTransfer;

  const overall = pctOrNull(adj.ppa.overall);
  const pick = (v) => {
    const own = pctOrNull(v);
    if (own !== null) return { pct: own, inherited: false };
    return { pct: overall === null ? 0 : overall, inherited: overall !== null };
  };
  const dc = pick(adj.ppa.compute);
  const ds = pick(adj.ppa.storage);
  const dt = pick(adj.ppa.dataTransfer);

  const discountCompute = compute * (dc.pct / 100);
  const discountStorage = storage * (ds.pct / 100);
  const discountTransfer = dataTransfer * (dt.pct / 100);
  const discount = discountCompute + discountStorage + discountTransfer;
  const afterDiscount = list - discount;

  const supportPct = pctOrNull(adj.support.pct);
  const support = supportPct === null ? 0 : afterDiscount * (supportPct / 100);

  const additionalLines = (adj.lines || []).filter((l) => !isBlank(l.name) || numOr0(l.amount) > 0);
  const additional = additionalLines.reduce((a, l) => a + numOr0(l.amount), 0);

  const effective = afterDiscount + support + additional;
  return {
    compute, storage, dataTransfer, list,
    dc, ds, dt,
    discountCompute, discountStorage, discountTransfer, discount, afterDiscount,
    supportPct, support,
    additional, additionalLines,
    effective,
    hasDiscount: discount > 0,
    hasAdjustments: discount > 0 || support > 0 || additional > 0,
    supportMissing: supportPct === null && !isBlank(adj.support.tier),
  };
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------
function Kpi({ label, value, sub, color = 'sectionHeader.main', basis }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" sx={{ color: GREY }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
      {basis ? (
        <Chip
          size="small"
          label={basis}
          sx={{ mt: 0.5, height: 20, fontSize: 11, fontWeight: 700, bgcolor: 'chipMuted.main', color: 'sectionHeader.main' }}
        />
      ) : null}
      {sub ? <Typography variant="body2" sx={{ color: GREY, mt: 0.5 }}>{sub}</Typography> : null}
    </Paper>
  );
}

function SectionTitle({ children, sx }) {
  return (
    <Typography variant="subtitle1" sx={{ ...HEAD, fontSize: 15, ...(sx || {}) }}>{children}</Typography>
  );
}

function CapNote({ shown, total, what }) {
  if (total <= shown) return null;
  return (
    <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 1 }}>
      Showing {fmt(shown)} of {fmt(total)} {what} on this page — every total, chart and export above covers all {fmt(total)}.
    </Typography>
  );
}

// One row of the waterfall, with a proportional bar so the shape of the deal is
// legible without a chart library.
function WaterfallRow({ label, amount, note, color, max, bold }) {
  const width = max > 0 ? Math.min(100, (Math.abs(amount) / max) * 100) : 0;
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: bold ? 700 : 500, whiteSpace: 'nowrap' }}>{label}</TableCell>
      <TableCell sx={{ width: '45%' }}>
        <Box sx={{ height: 10, borderRadius: 5, bgcolor: color, width: `${width}%`, minWidth: width > 0 ? 4 : 0 }} />
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 600, color, whiteSpace: 'nowrap' }}>
        {amount < 0 ? `(${money(Math.abs(amount))})` : money(amount)}
      </TableCell>
      <TableCell sx={{ color: GREY, fontSize: 12 }}>{note}</TableCell>
    </TableRow>
  );
}

// ===========================================================================
// Component
// ===========================================================================
export default function CostModel({ tenantId }) {
  const [tab, setTab] = useState(0);

  // ---- source data ---------------------------------------------------------
  const [servers, setServers] = useState([]);
  const [csgs, setCsgs] = useState([]);
  const [loadingFacts, setLoadingFacts] = useState(true);

  // ---- estimate controls ---------------------------------------------------
  const [source, setSource] = useState('current');       // 'current' | 'target'
  const [csgId, setCsgId] = useState('');
  const [cloud, setCloud] = useState('aws');
  const [region, setRegion] = useState(FALLBACK_RATE_CARD.aws.defaultRegion);
  const [defaultHours, setDefaultHours] = useState(HOURS_ALWAYS_ON);
  const [hourOverrides, setHourOverrides] = useState({});
  const [linePage, setLinePage] = useState(0);
  const [lineRpp, setLineRpp] = useState(ROWS_PER_PAGE);
  const [srcPage, setSrcPage] = useState(0);
  const [srcRpp, setSrcRpp] = useState(ROWS_PER_PAGE);

  // ---- live pricing --------------------------------------------------------
  // status: 'idle' | 'loading' | 'live' | 'unavailable'
  const [pricing, setPricing] = useState({ status: 'idle', payload: null, error: '' });
  const [priceNonce, setPriceNonce] = useState(0);

  // ---- adjustments ---------------------------------------------------------
  const [adj, setAdj] = useState(() => readAdjustments(adjKey(tenantId)));

  // -------------------------------------------------------------------------
  // Load fact sheets (tenant-scoped by the store) + the CSG bill of materials
  // -------------------------------------------------------------------------
  useEffect(() => {
    let live = true;
    setLoadingFacts(true);
    store.listFactSheets(tenantId, { type: 'ITComponent' }).then((list) => {
      if (!live) return;
      // ITComponent covers more than servers; keep the rows that either say
      // they are a server or actually carry sizing.
      const rows = (list || []).filter((s) => {
        const f = fieldsOf(s);
        return /server|vm|virtual machine|compute/i.test(String(f.category || ''))
          || f.vcpu !== undefined || f.ramGb !== undefined || f.storageGb !== undefined;
      });
      setServers(rows);
      setLoadingFacts(false);
    }).catch(() => { if (live) { setServers([]); setLoadingFacts(false); } });
    return () => { live = false; };
  }, [tenantId]);

  useEffect(() => {
    // Target-state BOM is owned by the Epic CSG screen; read-only here.
    let list = [];
    try {
      const raw = localStorage.getItem(`primordia.csg.${tenantId}`);
      const parsed = raw ? JSON.parse(raw) : null;
      list = parsed && Array.isArray(parsed.csgs) ? parsed.csgs : [];
    } catch (e) { list = []; }
    setCsgs(list);
    setCsgId(list.length ? list[0].id : '');
  }, [tenantId]);

  useEffect(() => {
    setAdj(readAdjustments(adjKey(tenantId)));
    setHourOverrides({});
    setLinePage(0);
    setSrcPage(0);
  }, [tenantId]);

  // The state update stays immediate — the screen must react as you type — but
  // the localStorage write is debounced. It was a synchronous JSON.stringify +
  // setItem per CHARACTER, on every Adjustments field and every SKU box.
  const persistTimer = useRef(null);
  const persistAdj = useCallback((next) => {
    setAdj(next);
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      writeAdjustments(adjKey(tenantId), next);
      persistTimer.current = null;
    }, 400);
  }, [tenantId]);

  // A pending write must not be dropped when the screen unmounts mid-typing.
  useEffect(() => () => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
  }, []);

  // -------------------------------------------------------------------------
  // Lines
  // -------------------------------------------------------------------------
  const activeCsg = useMemo(
    () => csgs.find((c) => c.id === csgId) || null,
    [csgs, csgId]
  );

  const built = useMemo(
    () => (source === 'target' ? buildTargetStateLines(activeCsg) : buildCurrentStateLines(servers)),
    [source, activeCsg, servers]
  );

  // Hours and the SKU mapping are the only consultant inputs on a priced row:
  // a scheduling decision and a naming decision, neither of them invented data.
  // Both are sparse override maps so memory does not grow with the estate.
  const lines = useMemo(() => {
    const skuMap = adj.skus || {};
    // `legacyId` is the line id from before the client became part of the key.
    // A consultant's saved SKU mapping is real work; falling back to the old id
    // carries it across rather than silently resetting every mapping to blank.
    const savedSku = (l) => (skuMap[l.id] !== undefined
      ? skuMap[l.id]
      : (l.legacyId !== undefined ? skuMap[l.legacyId] : undefined));
    return built.lines.map((l) => ({
      ...l,
      hours: l.kind === 'storage' ? 0 : (hourOverrides[l.id] === undefined ? defaultHours : hourOverrides[l.id]),
      sku: savedSku(l) === undefined ? l.sku : savedSku(l),
      diskType: l.diskType || adj.diskType || '',
    }));
  }, [built.lines, hourOverrides, defaultHours, adj.skus, adj.diskType]);

  const serverCount = useMemo(
    () => (source === 'target'
      ? lines.filter((l) => l.kind === 'compute').reduce((a, l) => a + l.qty, 0)
      : lines.reduce((a, l) => a + l.qty, 0)),
    [lines, source]
  );

  // The master workspace reads across clients, so a total computed here is a
  // sum over several estates. No line blends them any more, but the headline
  // figure still spans them and has to say so — it is the number that ends up
  // pasted into a deck.
  const isMaster = tenantId === 'optimum';
  const clientsInEstimate = useMemo(
    () => [...new Set(lines.map((l) => l.tenantId).filter(Boolean))].sort(),
    [lines]
  );

  // -------------------------------------------------------------------------
  // POST /pricing — attempted whenever the request changes, debounced.
  // Deliberately sends NO discountPct / supportUpliftPct: the service is asked
  // for list price only, and the whole discount story is built, shown and
  // labelled on the Adjustments tab where the client can see every step.
  // A 404/501 (endpoint not built or not bundled yet) and any other failure are
  // handled the same honest way: say so, then fall back to the internal rate
  // card with that fact on screen.
  // -------------------------------------------------------------------------
  const priceRequest = useMemo(() => ({
    op: 'estimate',
    cloud,
    region,
    hoursPerMonth: defaultHours,
    lines: lines.map((l) => {
      const line = {
        id: l.id,
        name: l.label,
        quantity: l.qty,
        hoursPerMonth: l.kind === 'storage' ? undefined : l.hours,
      };
      if (l.kind === 'compute') {
        if (cloud === 'aws') line.instanceType = l.sku || undefined;
        else line.sku = l.sku || undefined;
        if (l.storageKnown && l.diskType) {
          line.osDiskType = l.diskType;
          line.osDiskGb = l.storageGb;
        }
      } else if (l.storageKnown && l.diskType) {
        line.storage = [{ diskType: l.diskType, capacityGb: l.storageGb, quantity: 1 }];
      }
      return line;
    }),
  }), [cloud, region, lines, defaultHours]);

  useEffect(() => {
    if (!priceRequest.lines.length) {
      setPricing({ status: 'idle', payload: null, error: '' });
      return undefined;
    }
    let live = true;
    setPricing((p) => ({ ...p, status: 'loading' }));
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${getConfig().apiUrl}/pricing`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}` },
          body: JSON.stringify(priceRequest),
        });
        if (res.status === 404) throw new Error('POST /pricing is not implemented on this API yet (404).');
        if (res.status === 501) throw new Error('POST /pricing exists but its pricing SDK is not deployed yet (501).');
        const text = await res.text();
        // A non-JSON error page must not throw SyntaxError before the status
        // below is read - that reported "Unexpected token <" instead of the 502.
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        if (!res.ok) throw new Error((data && (data.error || data.message)) || `pricing request failed (${res.status})`);
        if (!data || !Array.isArray(data.lines)) throw new Error('The pricing service responded without a lines array.');
        if (!live) return;
        setPricing({ status: 'live', payload: data, error: '' });
      } catch (err) {
        if (!live) return;
        setPricing({ status: 'unavailable', payload: null, error: err.message || String(err) });
      }
    }, 600);
    return () => { live = false; clearTimeout(timer); };
  }, [priceRequest, priceNonce]);

  // -------------------------------------------------------------------------
  // Priced result — live when we have it, labelled internal estimate when not
  // -------------------------------------------------------------------------
  const priced = useMemo(() => {
    if (pricing.status === 'live' && pricing.payload) {
      const merged = mergeLivePricing(lines, pricing.payload);
      const t = pricing.payload.totals || {};
      return {
        ...merged,
        basis: 'live',
        basisLabel: merged.source ? `Live pricing (${merged.source})` : 'Live pricing',
        currency: t.currency || pricing.payload.currency || 'USD',
        retrievedAt: pricing.payload.retrievedAt || pricing.payload.priceRetrievedAt || '',
      };
    }
    const rate = FALLBACK_RATE_CARD[cloud];
    const rows = [];
    const unpriced = [];
    const caveats = [];
    lines.forEach((line) => {
      const r = fallbackPriceLine(line, rate);
      rows.push({ line, compute: r.compute, storage: r.storage, total: r.total, priced: r.priced });
      if (!r.priced) unpriced.push({ id: line.id, tenantId: line.tenantId, label: line.label, sku: line.sku, qty: line.qty, reason: r.reason });
      else if (r.caveat) caveats.push(`${line.label} — ${r.caveat}`);
    });
    return {
      rows,
      unpriced,
      caveats,
      totals: {
        compute: rows.reduce((a, r) => a + r.compute, 0),
        storage: rows.reduce((a, r) => a + r.storage, 0),
        total: rows.reduce((a, r) => a + r.total, 0),
      },
      basis: 'internal',
      basisLabel: 'Internal estimate (fallback rate card)',
      currency: 'USD',
      retrievedAt: '',
      complete: unpriced.length === 0,
      warnings: [],
      assumptions: [
        `${defaultHours} hours per month per running resource unless overridden on the line`,
        'on-demand list price shape only — no SKU, no reservations, no savings plans, no hybrid benefit',
        'internal rate card, not a published price: treat as an order of magnitude',
      ],
      cached: false,
      source: 'primordia-internal-rate-card',
    };
  }, [pricing, lines, cloud, defaultHours]);

  // Servers whose fact sheet has no sizing at all never reach a line, so they
  // are surfaced explicitly rather than vanishing from the denominator.
  const unsizedCount = built.noSizing.length;
  const noStorageCount = useMemo(
    () => lines.filter((l) => !l.storageKnown).reduce((a, l) => a + l.qty, 0),
    [lines]
  );

  const waterfall = useMemo(() => buildWaterfall(priced.totals, adj), [priced.totals, adj]);

  // -------------------------------------------------------------------------
  // Paged slices — the only rows that ever mount
  // -------------------------------------------------------------------------
  const pagedLines = useMemo(
    () => priced.rows.slice(linePage * lineRpp, (linePage * lineRpp) + lineRpp),
    [priced.rows, linePage, lineRpp]
  );
  const pagedServers = useMemo(
    () => servers.slice(srcPage * srcRpp, (srcPage * srcRpp) + srcRpp),
    [servers, srcPage, srcRpp]
  );

  const setHours = (id, raw) => {
    const n = numOrNull(raw);
    setHourOverrides((prev) => ({ ...prev, [id]: n === null ? 0 : n }));
  };
  const applyHoursToAll = (h) => { setDefaultHours(h); setHourOverrides({}); };

  // Mapping a sizing shape to a real published SKU is what makes live pricing
  // possible for current-state estates. It is saved with the tenant's other
  // cost settings and is always the consultant's call, never a guess.
  const setSku = (id, value) => persistAdj({ ...adj, skus: { ...(adj.skus || {}), [id]: value } });

  const mappedSkuCount = useMemo(() => lines.filter((l) => l.kind === 'compute' && l.sku).length, [lines]);
  const computeLineCount = useMemo(() => lines.filter((l) => l.kind === 'compute').length, [lines]);

  const changeCloud = (next) => {
    setCloud(next);
    setRegion(FALLBACK_RATE_CARD[next].defaultRegion);
  };

  // -------------------------------------------------------------------------
  // Adjustment editors
  // -------------------------------------------------------------------------
  const setPpa = (key, value) => persistAdj({ ...adj, ppa: { ...adj.ppa, [key]: value } });
  const setSupport = (key, value) => persistAdj({ ...adj, support: { ...adj.support, [key]: value } });
  const addLine = () => persistAdj({
    ...adj,
    lines: [...adj.lines, { id: newId('adj'), name: '', category: ADDITIONAL_CATEGORIES[0], amount: '' }],
  });
  const setLine = (id, key, value) => persistAdj({
    ...adj,
    lines: adj.lines.map((l) => (l.id === id ? { ...l, [key]: value } : l)),
  });
  const removeLine = (id) => persistAdj({ ...adj, lines: adj.lines.filter((l) => l.id !== id) });

  // -------------------------------------------------------------------------
  // Exports — CSV on every tab, and every export states its basis
  // -------------------------------------------------------------------------
  const basisTag = priced.basis === 'live'
    ? `Live pricing${priced.retrievedAt ? ` retrieved ${priced.retrievedAt}` : ' (no retrieval timestamp supplied)'}`
    : 'INTERNAL ESTIMATE — fallback rate card, not a quote';
  const floorTag = priced.complete ? '' : ` — FLOOR ONLY, ${priced.unpriced.length} item(s) carry no price`;

  const exportEstimateCsv = () => {
    const rows = [
      ...priced.rows.map((r) => ({
        section: 'Priced line',
        client: r.line.tenantId || '',
        label: r.line.label,
        sku: r.line.sku,
        qty: r.line.qty,
        vcpu: r.line.vcpu === null ? '' : r.line.vcpu,
        ram: r.line.ramGb === null ? '' : r.line.ramGb,
        storage: r.line.storageKnown ? r.line.storageGb : 'not recorded',
        hours: r.line.kind === 'storage' ? '' : r.line.hours,
        compute: r.priced ? r.compute.toFixed(2) : '',
        storageCost: r.priced ? r.storage.toFixed(2) : '',
        total: r.priced ? r.total.toFixed(2) : '',
        note: r.priced ? '' : 'UNPRICED — excluded from total',
      })),
      ...priced.unpriced.map((u) => ({
        section: 'Unpriced',
        client: u.tenantId || '',
        label: u.label,
        sku: u.sku,
        qty: u.qty,
        vcpu: '', ram: '', storage: '', hours: '', compute: '', storageCost: '', total: '',
        note: u.reason,
      })),
      {
        section: `Total (list price)${floorTag}`,
        // In the master workspace this total spans clients. Say so in the row
        // itself: a CSV outlives the screen it came from.
        client: isMaster ? `ALL CLIENTS (${clientsInEstimate.join(', ') || 'none'})` : tenantId,
        label: `${FALLBACK_RATE_CARD[cloud].label} ${region} — ${source === 'target' ? 'target-state BOM' : 'current-state fact sheets'}`,
        sku: '', qty: '', vcpu: '', ram: '', storage: '', hours: '',
        compute: priced.totals.compute.toFixed(2),
        storageCost: priced.totals.storage.toFixed(2),
        total: priced.totals.total.toFixed(2),
        note: `${basisTag}; ${priced.unpriced.length} unpriced line(s) NOT included`,
      },
    ];
    exportCsv('primordia-cost-estimate.csv', rows, [
      { label: 'Section', value: (r) => r.section },
      { label: 'Client', value: (r) => r.client },
      { label: 'Line', value: (r) => r.label },
      { label: 'SKU', value: (r) => r.sku },
      { label: 'Quantity', value: (r) => r.qty },
      { label: 'vCPU each', value: (r) => r.vcpu },
      { label: 'RAM GB each', value: (r) => r.ram },
      { label: 'Storage GB each', value: (r) => r.storage },
      { label: 'Hours/month', value: (r) => r.hours },
      { label: 'Compute monthly (list)', value: (r) => r.compute },
      { label: 'Storage monthly (list)', value: (r) => r.storageCost },
      { label: 'Total monthly (list)', value: (r) => r.total },
      { label: 'Note', value: (r) => r.note },
    ]);
  };

  const exportActualCsv = () => {
    exportCsv('primordia-actual-spend-connection-requirements.csv', CONNECTION_REQUIREMENTS, [
      { label: 'Cloud', value: (r) => r.cloud },
      { label: 'Requirement', value: (r) => r.requirement },
      { label: 'Detail', value: (r) => r.detail },
      { label: 'Status', value: () => 'Not connected — capability not built' },
    ]);
  };

  const exportAdjustmentsCsv = () => {
    const w = waterfall;
    const steps = [
      { step: 'List price — compute', amount: w.compute, note: `${basisTag}${floorTag}` },
      { step: 'List price — storage', amount: w.storage, note: `${basisTag}${floorTag}` },
      { step: 'List price — data transfer', amount: w.dataTransfer, note: isBlank(adj.dataTransferList) ? 'Not entered — not modelled by the estimate' : 'User-entered list egress' },
      { step: 'List price — total', amount: w.list, note: `LIST; ${priced.unpriced.length} unpriced line(s) excluded` },
      { step: 'Less PPA/EDP — compute', amount: -w.discountCompute, note: `${w.dc.pct}%${w.dc.inherited ? ' (overall rate)' : ' (category override)'}` },
      { step: 'Less PPA/EDP — storage', amount: -w.discountStorage, note: `${w.ds.pct}%${w.ds.inherited ? ' (overall rate)' : ' (category override)'}` },
      { step: 'Less PPA/EDP — data transfer', amount: -w.discountTransfer, note: `${w.dt.pct}%${w.dt.inherited ? ' (overall rate)' : ' (category override)'}` },
      { step: 'Subtotal after discount', amount: w.afterDiscount, note: `Agreement ref: ${adj.ppa.note || '(none)'}; effective ${adj.ppa.effectiveDate || '(not set)'}` },
      { step: 'Plus support', amount: w.support, note: w.supportPct === null ? 'NO PERCENTAGE ENTERED — support excluded' : `${adj.support.tier || 'tier not set'} at ${w.supportPct}% of post-discount spend (user-entered)` },
      ...w.additionalLines.map((l) => ({ step: `Plus ${l.name || 'additional line'}`, amount: numOr0(l.amount), note: l.category || '' })),
      { step: 'EFFECTIVE COST (monthly)', amount: w.effective, note: 'EFFECTIVE — includes discount, support and additional lines' },
      { step: 'EFFECTIVE COST (annual)', amount: w.effective * 12, note: '12 x monthly effective run-rate' },
    ];
    exportCsv('primordia-cost-adjustments.csv', steps, [
      { label: 'Step', value: (r) => r.step },
      { label: 'Monthly amount (USD)', value: (r) => r.amount.toFixed(2) },
      { label: 'Basis / note', value: (r) => r.note },
    ]);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const rate = FALLBACK_RATE_CARD[cloud];
  const maxBar = Math.max(waterfall.list, waterfall.effective, 1);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={HEAD}>Cloud Cost Model</Typography>
        <HelpBubble topic="costModel" />
        <Chip
          size="small"
          label={priced.basis === 'live' ? 'Live pricing' : 'Internal estimate — not a quote'}
          sx={{ bgcolor: priced.basis === 'live' ? '#e8f3ea' : '#eef3f9', color: priced.basis === 'live' ? GREEN : 'sectionHeader.main', fontWeight: 700 }}
        />
        {priced.retrievedAt ? (
          <Chip size="small" variant="outlined" label={`Prices retrieved ${priced.retrievedAt}`} sx={{ color: GREY }} />
        ) : null}
      </Stack>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label={<TabLabel text="Estimate" topic="tabCostEstimate" />} sx={{ fontWeight: 700 }} />
        <Tab label={<TabLabel text="Actual" topic="tabCostActual" />} sx={{ fontWeight: 700 }} />
        <Tab label={<TabLabel text="Adjustments" topic="tabCostAdjustments" />} sx={{ fontWeight: 700 }} />
      </Tabs>

      {/* ================================================================== */}
      {/* TAB 0 — ESTIMATE                                                    */}
      {/* ================================================================== */}
      {tab === 0 && (
        <Box>
          {pricing.status === 'loading' && <LinearProgress sx={{ mb: 2 }} />}

          {priced.basis !== 'live' && (
            <Alert severity="info" sx={{ mb: 2 }} action={(
              <Button size="small" startIcon={<RefreshIcon />} onClick={() => setPriceNonce((n) => n + 1)}>
                Retry
              </Button>
            )}>
              <AlertTitle>Live pricing is not wired up yet — this total is an internal estimate</AlertTitle>
              This screen asks <code>POST /pricing</code> for real {rate.label} rates in <code>{region}</code>.
              {pricing.error ? <> The call did not succeed: <em>{pricing.error}</em></> : ' The call has not returned a usable response.'}
              {' '}Until that endpoint is live, every number below comes from Primordia&apos;s internal fallback rate card
              ({money2(rate.computeVcpuHour)}/vCPU-hr + {money2(rate.computeGbRamHour)}/GB-RAM-hr,
              {' '}{money2(rate.storageGbMonth)}/GB-month storage, general-purpose on-demand Linux, no commitments).
              It is <strong>planning-grade and is not a quote</strong>. It knows nothing about SKUs, regions,
              reservations or your agreement, so do not put it in front of finance as a price.
            </Alert>
          )}

          {priced.basis === 'live' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Priced by <code>POST /pricing</code> for {rate.label} <code>{region}</code>
              {priced.retrievedAt
                ? <> — prices retrieved <strong>{priced.retrievedAt}</strong>{priced.cached ? ' (served from the pricing cache)' : ''}.</>
                : ' — the service did not report a retrieval timestamp, so price freshness is unknown.'}
              {' '}Amounts are {priced.currency} monthly <strong>list</strong> price, before any PPA/EDP discount, support or additional costs.
              {priced.assumptions.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {priced.assumptions.map((a, i) => (
                    <li key={`assumption-${i}`}><Typography variant="body2">{a}</Typography></li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {priced.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>The pricing service flagged this response</AlertTitle>
              {priced.warnings.map((w, i) => (
                <Typography key={`warning-${i}`} variant="body2">{w}</Typography>
              ))}
            </Alert>
          )}

          {computeLineCount > 0 && mappedSkuCount < computeLineCount && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{fmt(computeLineCount - mappedSkuCount)} of {fmt(computeLineCount)} compute line(s) have no instance type</AlertTitle>
              Live pricing prices <strong>published SKUs</strong>. Server fact sheets record a shape (vCPU/RAM), not an
              instance type, so those lines cannot be priced for real until someone maps them. Enter the instance type
              in the <strong>SKU</strong> column below — it is saved with this workspace — or size from a target-state
              bill of materials, which already carries cloud resource names. Primordia will not pick an instance type
              for you: choosing the family is a right-sizing decision, not a lookup.
            </Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField select size="small" label="Sizing source" value={source} onChange={(e) => { setSource(e.target.value); setLinePage(0); }} sx={{ minWidth: 240 }}>
              <MenuItem value="current">Current state — server fact sheets</MenuItem>
              <MenuItem value="target">Target state — bill of materials (CSG)</MenuItem>
            </TextField>
            {source === 'target' && (
              <TextField select size="small" label="Bill of materials" value={csgId} onChange={(e) => { setCsgId(e.target.value); setLinePage(0); }} sx={{ minWidth: 260 }} disabled={!csgs.length}>
                {csgs.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {`${c.label || 'CSG'} · ${c.envKind || ''} · ${String(c.importedAt || '').slice(0, 10)}`}
                  </MenuItem>
                ))}
                {!csgs.length && <MenuItem value="">No bill of materials imported</MenuItem>}
              </TextField>
            )}
            <TextField select size="small" label="Cloud" value={cloud} onChange={(e) => changeCloud(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="aws">AWS</MenuItem>
              <MenuItem value="azure">Azure</MenuItem>
            </TextField>
            <TextField size="small" label="Region" value={region} onChange={(e) => setRegion(e.target.value)} sx={{ minWidth: 150 }} />
            <Tooltip title="Disk type used to price storage on lines that do not state one (e.g. gp3, io2, Premium_LRS). Left blank, storage is not priced rather than guessed.">
              <TextField
                size="small"
                label="Default disk type"
                value={adj.diskType}
                onChange={(e) => persistAdj({ ...adj, diskType: e.target.value })}
                placeholder={cloud === 'aws' ? 'gp3' : 'Premium_LRS'}
                sx={{ minWidth: 160 }}
              />
            </Tooltip>
            <Tooltip title={`Set every compute line to ${HOURS_ALWAYS_ON} hours/month`}>
              <Button size="small" variant="outlined" onClick={() => applyHoursToAll(HOURS_ALWAYS_ON)}>Always-on ({HOURS_ALWAYS_ON}h)</Button>
            </Tooltip>
            <Tooltip title={`Set every compute line to ${HOURS_BUSINESS} hours/month (non-prod scheduled off)`}>
              <Button size="small" variant="outlined" onClick={() => applyHoursToAll(HOURS_BUSINESS)}>Business hours ({HOURS_BUSINESS}h)</Button>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportEstimateCsv}>CSV</Button>
            <Button size="small" startIcon={<PictureAsPdfIcon />} variant="outlined" onClick={() => printElementToPdf('costmodel-estimate-print', 'Primordia — Cloud Cost Estimate (list price)')}>PDF</Button>
          </Stack>

          {source === 'target' && !activeCsg && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No target-state bill of materials has been imported for this workspace. Import one on the
              <strong> Epic CSG target state</strong> screen, or size from the current-state fact sheets instead.
            </Alert>
          )}

          {source === 'current' && !loadingFacts && !servers.length && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No server (ITComponent) fact sheets carry sizing in this workspace. Load an RVTools or CMDB export on the
              <strong> Infrastructure import</strong> screen before estimating.
            </Alert>
          )}

          <div id="costmodel-estimate-print">
            {isMaster && clientsInEstimate.length > 1 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>
                  This estimate spans {fmt(clientsInEstimate.length)} clients — it is not any one client&apos;s number
                </AlertTitle>
                You are in the master workspace, so the figures below add up{' '}
                <strong>{clientsInEstimate.join(', ')}</strong>. Every line is scoped to a single client and labelled with
                it, but the totals are a cross-client sum. Switch to a client&apos;s portal for a figure that belongs in
                their deliverable.
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <Kpi label="Monthly" value={money(priced.totals.total)} color={BLUE}
                  basis={priced.complete ? 'LIST PRICE' : 'LIST PRICE — FLOOR ONLY'}
                  sub={priced.complete
                    ? `${fmt(lines.length)} line(s) covering ${fmt(serverCount)} resource(s)`
                    : `AT LEAST this much — ${fmt(priced.unpriced.length)} item(s) carry no price`} />
              </Grid>
              <Grid item xs={6} md={3}>
                <Kpi label="Annual" value={money(priced.totals.total * 12)} color={BLUE}
                  basis={priced.complete ? 'LIST PRICE' : 'LIST PRICE — FLOOR ONLY'}
                  sub="12 x monthly run-rate" />
              </Grid>
              <Grid item xs={6} md={3}>
                <Kpi label="Compute / storage split" value={`${money(priced.totals.compute)} / ${money(priced.totals.storage)}`}
                  basis="LIST PRICE" sub="Drives the per-category PPA discount" />
              </Grid>
              <Grid item xs={6} md={3}>
                <Kpi
                  label="After adjustments"
                  value={waterfall.hasAdjustments ? money(waterfall.effective) : '—'}
                  basis={waterfall.hasAdjustments ? 'EFFECTIVE COST' : 'NO ADJUSTMENTS ENTERED'}
                  color={waterfall.hasAdjustments ? GREEN : GREY}
                  sub={waterfall.hasAdjustments
                    ? `Includes ${waterfall.hasDiscount ? `${money(waterfall.discount)} PPA/EDP discount` : 'no discount'}${waterfall.support ? `, ${money(waterfall.support)} support` : ''}${waterfall.additional ? `, ${money(waterfall.additional)} additional` : ''}`
                    : 'Enter the agreement on the Adjustments tab'}
                />
              </Grid>
            </Grid>

            {priced.unpriced.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>{fmt(priced.unpriced.length)} line(s) could not be priced and are NOT in the total above</AlertTitle>
                The total is therefore an <strong>understatement</strong> of the modelled scope. Each one is listed in full below.
              </Alert>
            )}

            {unsizedCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>{fmt(unsizedCount)}</strong> server fact sheet(s) record no vCPU or RAM, so they carry no cost at all
                in this model — they are not zero-cost, they are unknown. Complete their sizing on the infrastructure import
                before this estimate is treated as covering the whole estate.
              </Alert>
            )}

            {noStorageCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>{fmt(noStorageCount)}</strong> resource(s) have no storage recorded. Their compute is priced;
                their storage is excluded rather than guessed.
              </Alert>
            )}

            {priced.caveats.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Partial pricing</AlertTitle>
                {priced.caveats.slice(0, 8).map((c, i) => (
                  <Typography key={`caveat-${i}`} variant="body2">{c}</Typography>
                ))}
                {priced.caveats.length > 8 && (
                  <Typography variant="body2" sx={{ color: GREY }}>
                    …and {fmt(priced.caveats.length - 8)} more (all present in the CSV export).
                  </Typography>
                )}
              </Alert>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
              <SectionTitle sx={{ mb: 0.5 }}>Priced lines — list price</SectionTitle>
              <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                {source === 'target'
                  ? 'One row per bill-of-materials line.'
                  : 'Server fact sheets are aggregated into one row per distinct vCPU / RAM / storage shape — exact match, nothing rounded.'}
                {' '}Only the hours/month field is editable, and only {fmt(lineRpp)} rows mount at a time, so the estate size
                never drives the number of inputs on screen. Totals and exports cover all {fmt(priced.rows.length)} rows.
              </Typography>
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={headerSx}>
                      <TableCell>Line</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">vCPU</TableCell>
                      <TableCell align="right">RAM GB</TableCell>
                      <TableCell align="right">Storage GB</TableCell>
                      <TableCell align="right">Hrs/mo</TableCell>
                      <TableCell align="right">Compute / mo</TableCell>
                      <TableCell align="right">Storage / mo</TableCell>
                      <TableCell align="right">Total / mo</TableCell>
                      <TableCell>Basis</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedLines.map((r) => (
                      <TableRow key={r.line.id} hover>
                        <TableCell sx={{ fontWeight: 600, maxWidth: 280 }}>
                          {isMaster && r.line.tenantId ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={r.line.tenantId}
                              sx={{ height: 18, fontSize: 10, mr: 0.75, verticalAlign: 'middle' }}
                            />
                          ) : null}
                          {r.line.label}
                          {r.line.sample.length > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', color: GREY }}>
                              e.g. {r.line.sample.join(', ')}{r.line.qty > r.line.sample.length ? ', …' : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.25, px: 0.5 }}>
                          <TextField
                            size="small"
                            value={r.line.sku}
                            onChange={(e) => setSku(r.line.id, e.target.value)}
                            placeholder={r.line.kind === 'storage' ? (cloud === 'aws' ? 'gp3' : 'Premium_LRS') : (cloud === 'aws' ? 'm6i.xlarge' : 'Standard_D4s_v5')}
                            inputProps={{ 'aria-label': `Instance type or SKU for ${r.line.label}`, style: { padding: '4px 6px', fontSize: 12, fontFamily: 'monospace' } }}
                            sx={{ width: 150 }}
                          />
                        </TableCell>
                        <TableCell align="right">{fmt(r.line.qty)}</TableCell>
                        <TableCell align="right">{r.line.vcpu === null ? '—' : fmt(r.line.vcpu)}</TableCell>
                        <TableCell align="right">{r.line.ramGb === null ? '—' : fmt(r.line.ramGb)}</TableCell>
                        <TableCell align="right">{r.line.storageKnown ? fmt(r.line.storageGb) : '—'}</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, px: 0.5 }}>
                          {r.line.kind === 'storage' ? '—' : (
                            <TextField
                              size="small"
                              type="number"
                              value={r.line.hours}
                              onChange={(e) => setHours(r.line.id, e.target.value)}
                              inputProps={{ min: 0, max: HOURS_ALWAYS_ON, 'aria-label': `Hours per month for ${r.line.label}`, style: { padding: '4px 6px', fontSize: 12, textAlign: 'right' } }}
                              sx={{ width: 78 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">{r.priced ? money(r.compute) : '—'}</TableCell>
                        <TableCell align="right">{r.priced ? money(r.storage) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: r.priced ? BLUE : (r.partial ? AMBER : RED) }}>
                          {r.priced || r.partial ? money(r.total) : 'unpriced'}
                        </TableCell>
                        <TableCell sx={{ color: GREY, fontSize: 11 }}>
                          {r.priced && priced.basisLabel}
                          {!r.priced && r.partial && 'Partly priced — the rest is in the unpriced list'}
                          {!r.priced && !r.partial && 'Unpriced — contributes nothing to the total'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!pagedLines.length && (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ color: GREY }}>
                          Nothing to price from this source yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={priced.rows.length}
                page={linePage}
                onPageChange={(e, p) => setLinePage(p)}
                rowsPerPage={lineRpp}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={(e) => { setLineRpp(parseInt(e.target.value, 10)); setLinePage(0); }}
              />
              <CapNote shown={Math.min(lineRpp, priced.rows.length)} total={priced.rows.length} what="priced lines" />
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <SectionTitle sx={{ mb: 0.5 }}>Unpriced items</SectionTitle>
              <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                Everything here is in scope but carries no price, and none of it is in the total above.
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={headerSx}>
                      <TableCell>Item</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Why it is unpriced</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {priced.unpriced.slice(0, ROWS_PER_PAGE).map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{u.label}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{u.sku || '—'}</TableCell>
                        <TableCell align="right">{fmt(u.qty)}</TableCell>
                        <TableCell sx={{ color: RED, fontSize: 12 }}>{u.reason}</TableCell>
                      </TableRow>
                    ))}
                    {!priced.unpriced.length && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ color: GREEN }}>
                          Every line in scope carries a price. The total above covers the full modelled scope.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <CapNote shown={ROWS_PER_PAGE} total={priced.unpriced.length} what="unpriced items" />
            </Paper>
          </div>

          {source === 'current' && (
            <Paper sx={{ p: 2 }}>
              <SectionTitle sx={{ mb: 0.5 }}>Source records</SectionTitle>
              <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                The fact sheets behind the aggregation, read-only and paged {fmt(srcRpp)} at a time.
                {' '}{fmt(servers.length)} server record(s) in this workspace.
              </Typography>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={headerSx}>
                      <TableCell>Name</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">vCPU</TableCell>
                      <TableCell align="right">RAM GB</TableCell>
                      <TableCell align="right">Storage GB</TableCell>
                      <TableCell>Power state</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedServers.map((s) => {
                      const f = fieldsOf(s);
                      const vcpu = numOrNull(f.vcpu);
                      const ram = numOrNull(f.ramGb);
                      const stor = numOrNull(f.storageGb);
                      return (
                        <TableRow key={s.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                          <TableCell sx={{ color: GREY, fontSize: 12 }}>{f.category || '—'}</TableCell>
                          <TableCell align="right" sx={{ color: vcpu === null ? RED : 'inherit' }}>{vcpu === null ? 'not recorded' : fmt(vcpu)}</TableCell>
                          <TableCell align="right" sx={{ color: ram === null ? RED : 'inherit' }}>{ram === null ? 'not recorded' : fmt(ram)}</TableCell>
                          <TableCell align="right" sx={{ color: stor === null ? AMBER : 'inherit' }}>{stor === null ? 'not recorded' : fmt(stor)}</TableCell>
                          <TableCell sx={{ color: GREY, fontSize: 12 }}>{f.powerState || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!pagedServers.length && (
                      <TableRow><TableCell colSpan={6} sx={{ color: GREY }}>No server fact sheets loaded.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={servers.length}
                page={srcPage}
                onPageChange={(e, p) => setSrcPage(p)}
                rowsPerPage={srcRpp}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={(e) => { setSrcRpp(parseInt(e.target.value, 10)); setSrcPage(0); }}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 1 — ACTUAL                                                      */}
      {/* ================================================================== */}
      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Not connected — this tab shows no spend because Primordia has none</AlertTitle>
            Actual spend can only come from the client&apos;s own billing data. Nothing on this tab is sample,
            demo or illustrative data, and nothing will appear here until a real account is connected.
            For a modelled number, use the <strong>Estimate</strong> tab and read it as an estimate.
          </Alert>

          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tooltip title="Account connection is not built yet — the access below is what it will require">
              <span>
                <Button variant="contained" disabled startIcon={<CloudOffIcon />} sx={primaryBtn}>
                  Connect account
                </Button>
              </span>
            </Tooltip>
            <Typography variant="body2" sx={{ color: GREY }}>Capability not built.</Typography>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportActualCsv}>CSV</Button>
          </Stack>

          <Paper sx={{ p: 3, mb: 2, textAlign: 'center' }}>
            <CloudOffIcon sx={{ fontSize: 44, color: GREY }} />
            <Typography variant="h6" sx={{ ...HEAD, mt: 1 }}>No cloud account is connected</Typography>
            <Typography variant="body2" sx={{ color: GREY, mt: 1, maxWidth: 780, mx: 'auto' }}>
              Real spend is read from the client&apos;s billing plane — Cost Explorer and CUR&nbsp;2.0 on AWS,
              Cost Management on Azure. Both need access granted inside the client&apos;s own tenant. The exact
              requirements are below so the client&apos;s cloud team can prepare them before the connector ships.
            </Typography>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {['AWS', 'Azure'].map((c) => (
              <Grid item xs={12} md={6} key={c}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <SectionTitle sx={{ mb: 1 }}>{c === 'AWS' ? 'AWS — what connecting requires' : 'Azure — what connecting requires'}</SectionTitle>
                  <Table size="small">
                    <TableBody>
                      {CONNECTION_REQUIREMENTS.filter((r) => r.cloud === c).map((r) => (
                        <TableRow key={`${r.cloud}-${r.requirement}`}>
                          <TableCell sx={{ fontWeight: 600, width: 190, verticalAlign: 'top' }}>{r.requirement}</TableCell>
                          <TableCell sx={{ color: GREY, fontSize: 13 }}>{r.detail}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Alert severity="success" icon={false} sx={{ mb: 2 }}>
            <AlertTitle>What Primordia will store</AlertTitle>
            For AWS: the <strong>role ARN</strong> and the <strong>ExternalId</strong> only. For Azure: the directory
            (tenant) ID, application (client) ID and the billing scope, authenticated by workload identity federation.
            <strong> No client credentials are ever stored</strong> — no access keys, no secrets, no passwords, no
            console logins. Access is read-only, scoped to cost data, and revocable by the client at any time by
            deleting the role or the app registration.
          </Alert>
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 2 — ADJUSTMENTS                                                 */}
      {/* ================================================================== */}
      {tab === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            List price is almost never what a client pays. This tab turns the list-price estimate into the
            <strong> effective cost</strong>: private pricing off list, the support plan on top, and the cost lines a
            cloud bill never shows. Everything here is entered by you and saved for this workspace only
            (<code>{adjKey(tenantId)}</code>). Nothing is defaulted to a rate we made up.
          </Alert>

          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={`List price ${money(waterfall.list)} / mo`} sx={{ fontWeight: 700, bgcolor: 'chipMuted.main', color: 'sectionHeader.main' }} />
            <Chip label={`Effective cost ${money(waterfall.effective)} / mo`} sx={{ fontWeight: 700, bgcolor: '#e8f3ea', color: GREEN }} />
            <Chip size="small" variant="outlined" label={priced.basis === 'live' ? 'List basis: live pricing' : 'List basis: internal estimate'} sx={{ color: GREY }} />
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportAdjustmentsCsv}>CSV</Button>
            <Button size="small" startIcon={<PictureAsPdfIcon />} variant="outlined" onClick={() => printElementToPdf('costmodel-adjust-print', 'Primordia — Effective Cost Waterfall')}>PDF</Button>
          </Stack>

          {priced.basis !== 'live' && waterfall.hasDiscount && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              A negotiated discount is being applied to an <strong>internal estimate</strong>, not to real prices.
              The percentage is exact; the number it is applied to is not. Wire up <code>POST /pricing</code> before
              this effective cost is presented as the client&apos;s price.
            </Alert>
          )}

          {priced.unpriced.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fmt(priced.unpriced.length)} line(s) on the Estimate tab are unpriced and therefore missing from the
              list price this waterfall starts from. The effective cost below is understated by whatever they cost.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <SectionTitle sx={{ mb: 0.5 }}>PPA / EDP discount</SectionTitle>
                <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
                  A percentage off list. Real private pricing agreements discount unevenly, so each service category
                  can override the overall rate; leave a category blank to inherit the overall percentage.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="Overall discount %"
                      value={adj.ppa.overall} onChange={(e) => setPpa('overall', e.target.value)}
                      helperText="Blank = no discount applied" inputProps={{ min: 0, max: 100, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Effective date" type="date"
                      value={adj.ppa.effectiveDate} onChange={(e) => setPpa('effectiveDate', e.target.value)}
                      InputLabelProps={{ shrink: true }} helperText="When these terms start" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" type="number" label="Compute %"
                      value={adj.ppa.compute} onChange={(e) => setPpa('compute', e.target.value)}
                      helperText={isBlank(adj.ppa.compute) ? `inherits ${waterfall.dc.pct}%` : 'override'}
                      inputProps={{ min: 0, max: 100, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" type="number" label="Storage %"
                      value={adj.ppa.storage} onChange={(e) => setPpa('storage', e.target.value)}
                      helperText={isBlank(adj.ppa.storage) ? `inherits ${waterfall.ds.pct}%` : 'override'}
                      inputProps={{ min: 0, max: 100, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" type="number" label="Data transfer %"
                      value={adj.ppa.dataTransfer} onChange={(e) => setPpa('dataTransfer', e.target.value)}
                      helperText={isBlank(adj.ppa.dataTransfer) ? `inherits ${waterfall.dt.pct}%` : 'override'}
                      inputProps={{ min: 0, max: 100, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Agreement reference / note"
                      value={adj.ppa.note} onChange={(e) => setPpa('note', e.target.value)}
                      placeholder="e.g. EDP #12345, 3-year, $X annual commit — signed by …"
                      helperText="Free text. Whoever reads the waterfall needs to know which paper it came from." />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <SectionTitle sx={{ mb: 0.5 }}>Support plan</SectionTitle>
                <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
                  Cloud support is billed as a tiered percentage of eligible spend, and those bands and rates change.
                  Primordia does not ship a rate for them — pick the tier and enter the blended percentage you have
                  confirmed from the provider or the invoice.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={7}>
                    <TextField select fullWidth size="small" label="Support tier"
                      value={adj.support.tier} onChange={(e) => setSupport('tier', e.target.value)}
                      helperText={`${FALLBACK_RATE_CARD[cloud].label} plans`}>
                      <MenuItem value="">None / not purchased</MenuItem>
                      {SUPPORT_TIERS[cloud].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField fullWidth size="small" type="number" label="Support %"
                      value={adj.support.pct} onChange={(e) => setSupport('pct', e.target.value)}
                      helperText="You enter this — no default"
                      inputProps={{ min: 0, max: 100, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" type="number" label="Data transfer / egress at list (monthly)"
                      value={adj.dataTransferList} onChange={(e) => persistAdj({ ...adj, dataTransferList: e.target.value })}
                      helperText="The estimate does not model egress. Enter list egress here if you have it, so the data-transfer discount has something to act on."
                      inputProps={{ min: 0, step: 1 }} />
                  </Grid>
                </Grid>
                {waterfall.supportMissing && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Tier <strong>{adj.support.tier}</strong> is selected but no percentage has been entered, so
                    <strong> no support cost is included</strong> in the effective cost below.
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <SectionTitle>Additional cost lines</SectionTitle>
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="contained" startIcon={<AddIcon />} sx={primaryBtn} onClick={addLine}>Add line</Button>
            </Stack>
            <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
              Everything the cloud bill does not carry: managed services, partner/MSP margin, licensing brought
              separately, egress billed elsewhere, third-party tooling. Monthly amounts.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={headerSx}>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Monthly amount</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adj.lines.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell sx={{ minWidth: 220 }}>
                        <TextField fullWidth size="small" value={l.name} placeholder="e.g. Managed Epic hosting"
                          onChange={(e) => setLine(l.id, 'name', e.target.value)}
                          inputProps={{ 'aria-label': 'Additional cost line name' }} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <TextField select fullWidth size="small" value={l.category || ADDITIONAL_CATEGORIES[0]}
                          onChange={(e) => setLine(l.id, 'category', e.target.value)}>
                          {ADDITIONAL_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 160 }}>
                        <TextField size="small" type="number" value={l.amount}
                          onChange={(e) => setLine(l.id, 'amount', e.target.value)}
                          inputProps={{ min: 0, step: 1, 'aria-label': 'Monthly amount', style: { textAlign: 'right' } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 56 }}>
                        <IconButton size="small" aria-label="Remove line" onClick={() => removeLine(l.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!adj.lines.length && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ color: GREY }}>
                        No additional cost lines. The effective cost below is cloud spend only.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <div id="costmodel-adjust-print">
            <Paper sx={{ p: 2 }}>
              <SectionTitle sx={{ mb: 0.5 }}>List price to effective cost</SectionTitle>
              <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                Basis of the list price: <strong>{basisTag}{floorTag}</strong>.
                {' '}Every line below is labelled list or effective; the effective figure is only ever shown
                alongside the discount, support and additional cost that produced it.
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headerSx}>
                      <TableCell>Step</TableCell>
                      <TableCell />
                      <TableCell align="right">Monthly</TableCell>
                      <TableCell>Basis</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <WaterfallRow label="List price — compute" amount={waterfall.compute} max={maxBar} color={BLUE} note={basisTag} />
                    <WaterfallRow label="List price — storage" amount={waterfall.storage} max={maxBar} color={BLUE} note={basisTag} />
                    <WaterfallRow label="List price — data transfer" amount={waterfall.dataTransfer} max={maxBar} color={BLUE}
                      note={isBlank(adj.dataTransferList) ? 'Not entered — egress is not modelled by the estimate' : 'User-entered list egress'} />
                    <WaterfallRow label="LIST PRICE" amount={waterfall.list} max={maxBar} color="sectionHeader.main" bold note="Before any agreement, support or additional cost" />
                    <WaterfallRow label="Less PPA / EDP — compute" amount={-waterfall.discountCompute} max={maxBar} color={GREEN}
                      note={`${waterfall.dc.pct}% ${waterfall.dc.inherited ? '(overall rate)' : '(category override)'}`} />
                    <WaterfallRow label="Less PPA / EDP — storage" amount={-waterfall.discountStorage} max={maxBar} color={GREEN}
                      note={`${waterfall.ds.pct}% ${waterfall.ds.inherited ? '(overall rate)' : '(category override)'}`} />
                    <WaterfallRow label="Less PPA / EDP — data transfer" amount={-waterfall.discountTransfer} max={maxBar} color={GREEN}
                      note={`${waterfall.dt.pct}% ${waterfall.dt.inherited ? '(overall rate)' : '(category override)'}`} />
                    <WaterfallRow label="Subtotal after discount" amount={waterfall.afterDiscount} max={maxBar} color="sectionHeader.main" bold
                      note={`${adj.ppa.note || 'no agreement reference recorded'}${adj.ppa.effectiveDate ? ` · effective ${adj.ppa.effectiveDate}` : ' · no effective date set'}`} />
                    <WaterfallRow label="Plus support" amount={waterfall.support} max={maxBar} color={AMBER}
                      note={waterfall.supportPct === null
                        ? 'No percentage entered — support is NOT included'
                        : `${adj.support.tier || 'tier not set'} at ${waterfall.supportPct}% of post-discount spend (user-entered)`} />
                    {waterfall.additionalLines.map((l) => (
                      <WaterfallRow key={l.id} label={`Plus ${l.name || 'additional line'}`} amount={numOr0(l.amount)}
                        max={maxBar} color={AMBER} note={l.category || ''} />
                    ))}
                    <WaterfallRow label="EFFECTIVE COST" amount={waterfall.effective} max={maxBar} color={GREEN} bold
                      note="What the client actually pays, per month, on these assumptions" />
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <Kpi label="Monthly" value={money(waterfall.effective)} basis="EFFECTIVE COST" color={GREEN}
                    sub={waterfall.hasDiscount ? `after ${money(waterfall.discount)} PPA/EDP discount` : 'no discount applied'} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Kpi label="Annual" value={money(waterfall.effective * 12)} basis="EFFECTIVE COST" color={GREEN}
                    sub="12 x monthly effective run-rate" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Kpi label="Annual at list" value={money(waterfall.list * 12)} basis="LIST PRICE" color="sectionHeader.main"
                    sub={waterfall.list > 0 ? `effective is ${(((waterfall.effective - waterfall.list) / waterfall.list) * 100).toFixed(1)}% vs list` : 'no list price yet'} />
                </Grid>
              </Grid>
            </Paper>
          </div>
        </Box>
      )}
    </Box>
  );
}
